import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Grid,
  CircularProgress,
} from '@mui/joy';
import {
  Star as StarIcon,
  Star as ReviewIcon,
  PaperPlaneTilt as SendIcon,
  SquaresFour as WidgetIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { MyReviews } from '../../components/marketing/reviews/MyReviews';
import { ReviewRequests } from '../../components/marketing/reviews/ReviewRequests';
import { ReviewFunnel } from '../../components/marketing/reviews/ReviewFunnel';
import { ReviewReports } from '../../components/marketing/reviews/ReviewReports';
import { ReviewWidgets } from '../../components/marketing/reviews/ReviewWidgets';
import { ReviewFeedback } from '../../components/marketing/reviews/ReviewFeedback';

interface ReviewStats {
  total_reviews: number;
  average_rating: number;
  reviews_by_platform: Record<string, number>;
  pending_requests: number;
  response_rate: number;
  recent_reviews: any[];
}

export const Reviews: React.FC = () => {
  // Tab lives in the URL (?tab=widgets) so other screens can deep-link
  // (e.g. the widget tab's "connect a platform" CTA, readiness fix links)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'reviews';
  const setActiveTab = (tab: string) => setSearchParams({ tab }, { replace: true });

  // Fetch review stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['review-stats'],
    queryFn: async () => {
      const response = await api.get<ReviewStats>('/api/marketing/reviews/stats/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (statsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const formatRating = (rating: number | undefined) => {
    if (!rating) return '0.0';
    return rating.toFixed(1);
  };

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={6} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Reviews
                  </Typography>
                  <Typography level="h3">{stats?.total_reviews || 0}</Typography>
                </Box>
                <ReviewIcon size={40} color="var(--joy-palette-primary-500)" style={{ opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={6} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Average Rating
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography level="h3">{formatRating(stats?.average_rating)}</Typography>
                    <StarIcon size={24} color="var(--joy-palette-warning-500)" />
                  </Box>
                </Box>
                <StarIcon size={40} color="var(--joy-palette-warning-500)" style={{ opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={6} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Published Reply Rate
                  </Typography>
                  <Typography level="h3">{stats?.response_rate?.toFixed(0) || 0}%</Typography>
                </Box>
                <SendIcon size={40} color="var(--joy-palette-success-500)" style={{ opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={6} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Pending Requests
                  </Typography>
                  <Typography level="h3">{stats?.pending_requests || 0}</Typography>
                </Box>
                <WidgetIcon size={40} color="var(--joy-palette-primary-500)" style={{ opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue as string)}
          sx={{ bgcolor: 'transparent' }}
        >
          <TabList
            sx={{
              flexWrap: 'wrap',
              '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } },
            }}
          >
            <Tab value="reviews">My Reviews</Tab>
            <Tab value="requests">Review Requests</Tab>
            <Tab value="funnel">Funnel</Tab>
            <Tab value="feedback">Feedback</Tab>
            <Tab value="reports">Reports</Tab>
            <Tab value="widgets">Widgets</Tab>
          </TabList>

          <TabPanel value="reviews">
            <MyReviews />
          </TabPanel>

          <TabPanel value="requests">
            <ReviewRequests />
          </TabPanel>

          <TabPanel value="funnel">
            <ReviewFunnel />
          </TabPanel>

          <TabPanel value="feedback">
            <ReviewFeedback />
          </TabPanel>

          <TabPanel value="reports">
            <ReviewReports />
          </TabPanel>

          <TabPanel value="widgets">
            <ReviewWidgets />
          </TabPanel>
        </Tabs>
      </Card>
    </Box>
  );
};